import type { ReactNode } from 'react'

import { createInertiaApp } from '@inertiajs/react'
import { createRoot } from 'react-dom/client'

import '../helpers/mui-license'
import '../helpers/polyfill'
import { DefaultLayout } from '../layouts/DefaultLayout'
import { StandaloneLayout } from '../layouts/StandaloneLayout'

type ResolvedComponent = {
  default: React.ComponentType<any> & {
    layout?: (page: ReactNode) => ReactNode
  }
}

// Lazy (not eager) so each page ships as its own chunk, loaded on demand instead of
// bundling every page in the app into a single entrypoint chunk.
const pages = import.meta.glob<ResolvedComponent>('../pages/**/*.{ts,tsx}')

createInertiaApp({
  id: 'app',

  resolve: (name: string): Promise<ResolvedComponent['default']> => {
    const importPage =
      pages[`../pages/${name}/index.ts`] ||
      pages[`../pages/${name}/index.tsx`] ||
      pages[`../pages/${name}.ts`] ||
      pages[`../pages/${name}.tsx`]

    if (!importPage) throw new Error(`Missing Inertia page component: '${name}'`)

    return importPage().then((page) => {
      page.default.layout =
        page.default.layout ||
        ((node: ReactNode) => {
          const standalone = Boolean((node as any)?.props?.runtime?.standalone)
          const Layout = standalone ? StandaloneLayout : DefaultLayout
          return <Layout>{node}</Layout>
        })

      return page.default
    })
  },

  setup({ el, App, props }) {
    if (el) {
      createRoot(el).render(<App {...props} />)
    } else {
      console.error(
        'Missing root element.\n\n' +
          'If you see this error, it probably means you load Inertia.js on non-Inertia pages.\n' +
          'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.'
      )
    }
  },
})
